import { makeAutoObservable, runInAction } from 'mobx'
import { AgentActionType, type IAgentActionTagPayload } from 'entities/AgentActions'
import {
  ChatbotDisqualifyBehaviorStatus,
  ChatbotField,
  ChatbotInstructionType,
  type IChatbotFullData,
} from 'entities/Chatbot'
import { CustomFieldsStore } from 'entities/CustomField'
import { ContactsApi } from 'entities/Contacts'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { fetchSendFromInfo, type ISendFromInfo } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { KnowledgeBaseApi } from 'entities/KnowledgeBase'
import { IntegrationKey } from 'entities/Integrations'
import { buildGoogleCalendarMergeFields } from 'entities/MergeField'
import { EnrollmentFilters, initEnrollmentFilters } from 'widgets/ContactEnrollment'
import { MessageFieldStore, EnumVariantMessageField } from 'widgets/MessageField'
import { BookingFieldsStore } from 'pages/chatbot/store/bookingFieldsStore'

const FIELDS_LIMIT = 100

export class DetailsStore {
  private _fields!: ChatbotField[]
  private _fieldsStore = new CustomFieldsStore(FIELDS_LIMIT)

  private _futureSendFrom!: Promise<ISendFromInfo | null>
  private _futureContactsCount!: Promise<number | null>
  private _futureActionsTags = new Map<string, Promise<Tag[]>>()
  private _futureEnrollmentFilters: Promise<EnrollmentFilters> | null = null
  private _futureEnrollmentExcludeFilters: Promise<EnrollmentFilters> | null = null
  private _futureKnowledgeBaseDocumentNames: Promise<string[]> | null = null
  private _instructionMessageFieldStore: MessageFieldStore | null = null
  private _bookingFieldsStore: BookingFieldsStore | null = null
  private _bookingIntegrationKey: IntegrationKey | null = null
  private _futureBookingMergeFields: Promise<void> = Promise.resolve()

  constructor(private _data: IChatbotFullData, private _entities: IEntitiesStore) {
    const isUnified = _data.type === ChatbotInstructionType.Unified

    this._initFields()
    this._initSendFrom()
    this._initContactsCount()
    this._futureBookingMergeFields = this._initBookingMergeFields()

    if (isUnified) {
      this._initEnrollmentFilters()
      this._initEnrollmentExcludeFilters()
      this._initKnowledgeBaseDocumentNames()
      this._initInstructionMessageFieldStore()
      this._futureBookingMergeFields.then(this._refreshInstructionMessageFieldStore)
    } else {
      this._initActionTags()
    }

    makeAutoObservable(this)
  }

  get data() {
    return this._data
  }

  get fields() {
    return this._fields
  }

  get dataCollectionInstruction() {
    return this._data.settings?.data_collection_instruction?.value || ''
  }

  get qualifyInstruction() {
    return this._data.settings?.qualify_instruction?.value || ''
  }

  get qualifyEnabled() {
    return this._data.settings?.qualify_enabled?.value ?? false
  }

  get qualifiedActions() {
    return this._data.qualified_actions
  }

  get disqualifiedActions() {
    return this._data.disqualified_actions
  }

  get disqualifiedBehavior() {
    if (this._data.settings?.disqualify_behavior?.value === ChatbotDisqualifyBehaviorStatus.Stop) {
      return 'Politely conclude conversation'
    }

    if (
      this._data.settings?.disqualify_behavior?.value === ChatbotDisqualifyBehaviorStatus.Continue
    ) {
      return 'Continue to success or fallback actions'
    }

    return undefined
  }

  get futureSendFrom() {
    return this._futureSendFrom
  }

  get futureFutureContactsCount() {
    return this._futureContactsCount
  }

  get futureEnrollmentFilters() {
    return this._futureEnrollmentFilters
  }

  get futureEnrollmentExcludeFilters() {
    return this._futureEnrollmentExcludeFilters
  }

  get futureKnowledgeBaseDocumentNames() {
    return this._futureKnowledgeBaseDocumentNames
  }

  get instructionMessageFieldStore() {
    return this._instructionMessageFieldStore
  }

  get futureBookingMergeFields() {
    return this._futureBookingMergeFields
  }

  getFutureActionsTags = (ids: number[]) => {
    const key = ids.join(',')
    return this._futureActionsTags.get(key) ?? Promise.resolve([])
  }

  getFieldName = (fieldKey: string) => {
    return this._fieldsStore.fields.find(({ key }) => key === fieldKey)?.name
  }

  loadData = () => Promise.all([this._fieldsStore.loadData()])

  private _initFields = () => {
    this._fields = this._data.fields?.map(ChatbotField.Create) ?? []
    if (!this._fields.length) this._fields = [ChatbotField.Create()]
  }

  private _initSendFrom = () => {
    this._futureSendFrom = fetchSendFromInfo(this._data.send_from, this._entities)
  }

  private _initEnrollmentFilters = () => {
    const enrollmentFilters = EnrollmentFilters.Empty()

    this._futureEnrollmentFilters = (async () => {
      const data = await initEnrollmentFilters(this._entities, this._data.filters)

      if (data) {
        runInAction(() => {
          enrollmentFilters.sync(data)
        })
      }

      return enrollmentFilters
    })()
  }

  private _initEnrollmentExcludeFilters = () => {
    const enrollmentExcludeFilters = EnrollmentFilters.Empty()

    this._futureEnrollmentExcludeFilters = (async () => {
      const data = await initEnrollmentFilters(this._entities, this._data.exclude_filters)

      if (data) {
        runInAction(() => {
          enrollmentExcludeFilters.sync(data)
        })
      }

      return enrollmentExcludeFilters
    })()
  }

  private _initContactsCount = () => {
    const filters = this._data.filters
    const excludeFilters = this._data.exclude_filters

    const getFiltersSize = (groups: typeof filters) =>
      groups
        ? Object.values(groups)
            .map((filter) => filter.length ?? 0)
            .reduce((prev, next) => prev + next, 0)
        : 0

    if (!getFiltersSize(filters) && !getFiltersSize(excludeFilters)) {
      return (this._futureContactsCount = Promise.resolve(null))
    }

    this._futureContactsCount = ContactsApi.getContactsFiltersCount({
      filtersListGroups: filters ?? undefined,
      excludeFiltersListGroups: excludeFilters ?? undefined,
    }).then(
      ({ data: { count } }) => count,
      () => null
    )
  }

  private _initInstructionMessageFieldStore = () => {
    this._instructionMessageFieldStore = new MessageFieldStore(
      {
        noCounter: true,
        variant: EnumVariantMessageField.Clean,
        disableMergeFieldsFallback: true,
        resetOnUnmount: false,
      },
      this._entities
    )

    this._refreshInstructionMessageFieldStore()
  }

  private _refreshInstructionMessageFieldStore = () => {
    this._instructionMessageFieldStore?.setMessageData({
      message: this._data.settings?.instructions?.value ?? '',
      attachments: [],
      isFocus: false,
      isReplace: true,
      isReset: false,
      replaceActionChips: true,
      isDisableReplaceMention: true,
    })
  }

  private _initBookingMergeFields = async () => {
    const integrationKey = this._data.settings?.booking_provider?.value

    if (integrationKey === IntegrationKey.google_calendar) {
      const integration = this._entities.integrationsStore.getIntegration(
        IntegrationKey.google_calendar
      )
      if (!integration) return

      this._bookingIntegrationKey = IntegrationKey.google_calendar
      this._entities.mergeFieldStore.addMergeFieldsIntegrations(
        this._bookingIntegrationKey,
        buildGoogleCalendarMergeFields(integration.id)
      )
      return
    }

    const bookAppointmentLinkId = this._data.settings?.book_appointment_link?.id

    if (!bookAppointmentLinkId || !integrationKey) return

    this._bookingFieldsStore = new BookingFieldsStore(this._entities)

    await this._bookingFieldsStore.fetchFields({
      params: { id: bookAppointmentLinkId, integrationKey },
    })

    this._bookingIntegrationKey = IntegrationKey[integrationKey]
    this._entities.mergeFieldStore.addMergeFieldsIntegrations(
      this._bookingIntegrationKey,
      this._bookingFieldsStore.hubspotMergeFields
    )
  }

  private _initKnowledgeBaseDocumentNames = async () => {
    const ids = this._data.knowledge_base_documents
    if (!ids?.length) {
      this._futureKnowledgeBaseDocumentNames = Promise.resolve([])
      return
    }
    this._futureKnowledgeBaseDocumentNames = (async () => {
      try {
        const { data } = await KnowledgeBaseApi.getKnowledgeBaseDocuments({ limit: 25, page: 1 })
        return data.data.filter((doc) => ids.includes(doc.id)).map((doc) => doc.name)
      } catch {
        return []
      }
    })()
  }

  private _initActionTags = () => {
    const allActions = [
      ...(this._data.success_actions ?? []),
      ...(this._data.fallback_actions ?? []),
      ...(this._data.qualified_actions ?? []),
      ...(this._data.disqualified_actions ?? []),
    ]

    allActions
      .filter(
        (action) =>
          action.type === AgentActionType.AddTag || action.type === AgentActionType.RemoveTag
      )
      .map((action) => action.settings as IAgentActionTagPayload)
      .forEach((settings) => {
        const ids = settings.tag_ids
        const key = ids.join(',')
        const futureTags = TagsApi.getTagsById(ids).then(
          (tags) => tags.map((tag) => new Tag(tag)),
          () => []
        )

        this._futureActionsTags.set(key, futureTags)
      })
  }

  dispose = () => {
    if (this._bookingIntegrationKey) {
      this._entities.mergeFieldStore.removeDynamicMergeFields(this._bookingIntegrationKey)
      this._bookingIntegrationKey = null
    }
  }
}
